import { Colors } from '@brg/ui'
import { LayoutConst } from 'core'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { CreateLinkTo } from 'shared/constants/linkTo'
import styled, { DefaultTheme, useTheme } from 'styled-components'

const ErrorPageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`

const ErrorTitle = styled.div<{
    noTheme?: boolean
}>`
    font-family: Inter;
    font-size: 68px;
    font-weight: 600;
    line-height: 64px;
    letter-spacing: -0.02em;
    margin-top: 120px;
    text-align: center;
    color: ${p => p.noTheme ? Colors.galBlack73 : p.theme.errorPage.title};

    @media (max-width: ${LayoutConst?.screen?.mac13 || 1440}px) {
        margin-top: 176px;
        font-size: 51px;
        line-height: 60px;
    }

    @media (max-width: ${LayoutConst?.screen?.sm || 568}px) {
        margin-top: 98px;
        font-size: 28px;
        line-height: 34px;
        letter-spacing: 0px;
    }
`

const ErrorSubtitle = styled.div<{
    noTheme?: boolean
}>`
    font-family: Inter;
    font-size: 43px;
    font-weight: 600;
    line-height: 64px;
    letter-spacing: -0.02em;
    text-align: center;
    margin-top: 24px;
    color: ${p => p.noTheme ? Colors.galBlack73 : p.theme.errorPage.subtitle};

    @media (max-width: ${LayoutConst?.screen?.mac13 || 1440}px) {
        font-size: 34px;
        line-height: 41px;
        margin-top: 8px;
    }

    @media (max-width: ${LayoutConst?.screen?.sm || 568}px) {
        font-size: 17px;
        line-height: 22px;
        margin-bottom: 12px;
    }
`

const ErrorText = styled.div<{
    noTheme?: boolean
}>`
    font-family: Inter;
    font-size: 30px;
    font-weight: 400;
    line-height: 40px;
    letter-spacing: -0.02em;
    text-align: center;
    margin-top: 32px;
    color: ${p => p.noTheme ? Colors.galBlack47 : p.theme.errorPage.text};
    white-space: pre-line;

    @media (max-width: ${LayoutConst?.screen?.mac13 || 1440}px) {
        font-size: 17px;
        line-height: 22px;
        margin-top: 8px;
    }

    @media (max-width: ${LayoutConst?.screen?.sm || 568}px) {
        font-size: 13px;
        line-height: 16px;
        letter-spacing: 0px;
        margin-top: 12px;
    }
`

export const ErrorButtonsWrapper = styled.div`
    display: flex;
    gap: 24px;
    margin-top: 64px;

    @media (max-width: ${LayoutConst?.screen?.sm || 568}px) {
        gap: 10px;
        margin-top: 40px;
    }
`

const ErrorButton = styled.div<{
    noTheme?: boolean
}>`
    cursor: pointer;
    font-family: Inter;
    font-size: 17px;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: -0.02em;
    text-align: center;
    border-radius: 3px;
    padding: 12px 48px;

    @media (max-width: ${LayoutConst?.screen?.sm || 568}px) {
        padding: 12px 36.5px;
    }
`

const ErrorButtonHollow = styled(ErrorButton)`
    border: 1px solid ${p => p => p.noTheme ? Colors.galBlack31 : p.theme.errorPage.buttons.border};
    color: ${p => p.noTheme ? Colors.zel : p.theme.errorPage.buttons.hollowText};
`

const ErrorButtonFilled = styled(ErrorButton)`
    background-color: ${p => p.noTheme ? Colors.galBlack100 : p.theme.errorPage.buttons.bg};
    color: ${p => p.noTheme ? "#fff" : p.theme.errorPage.buttons.filledText};
`

export enum ErrorPageButtons {
    BACK_MAIN,
    REFRESH,
    NOTHING,
    MAIN_ONLY,
    PROFILE,
    EDIT
}

interface ErrorPageProps {
    title?: string
    subtitle?: string
    text?: string
    id?: string
    buttons?: ErrorPageButtons
    noTheme?: boolean
    onBackClick?: () => void;
}

export const ErrorPage: FC<ErrorPageProps> = ({title, subtitle, text, id, buttons = ErrorPageButtons.BACK_MAIN, noTheme, onBackClick }) => {
    const { t } = useTranslation()
    const history = useHistory()

  return (
    <ErrorPageWrapper>
        {title && <ErrorTitle noTheme={noTheme}>{title}</ErrorTitle>}
        {subtitle && <ErrorSubtitle noTheme={noTheme}>{subtitle}</ErrorSubtitle>}
        {text && <ErrorText noTheme={noTheme}>{text}</ErrorText>}
        <ErrorButtonsWrapper>
            {buttons === ErrorPageButtons.MAIN_ONLY &&
                <ErrorButtonFilled noTheme={noTheme} onClick={() => history.push("/")}>{noTheme ? "На главную" : t("errorpage.main")}</ErrorButtonFilled>
            }
            {buttons === ErrorPageButtons.BACK_MAIN &&
                <>
                    <ErrorButtonHollow noTheme={noTheme} onClick={() => {
                        onBackClick? onBackClick() : history.goBack()
                        // history.goBack()
                    }}>{noTheme ? "Назад" : t("errorpage.back")}</ErrorButtonHollow>
                    <ErrorButtonFilled noTheme={noTheme} onClick={() =>{
                        onBackClick && onBackClick()
                        history.push("/")
                    }}>{noTheme ? "На главную" : t("errorpage.main")}</ErrorButtonFilled>
                </>
            }
            {buttons === ErrorPageButtons.REFRESH &&
                <ErrorButtonFilled noTheme={noTheme} onClick={() => window.location.reload()}>{noTheme ? "Обновить" : t("errorpage.refresh")}</ErrorButtonFilled>
            }
            {buttons === ErrorPageButtons.PROFILE && (
                <>
                     <ErrorButtonHollow noTheme={noTheme} onClick={() => {
                        onBackClick? onBackClick() : history.goBack()
                        // history.goBack()
                    }}>{noTheme ? "Назад" : t("errorpage.back")}</ErrorButtonHollow>
                    <ErrorButtonFilled 
                        noTheme={noTheme} 
                        onClick={() => history.push(CreateLinkTo.designer(id ?? ''))}
                    >
                        {noTheme ? "Профиль" : t("errorpage.profile")}
                    </ErrorButtonFilled>
                </>
            )
                
            }
            {buttons === ErrorPageButtons.EDIT && (
                <>
                     <ErrorButtonHollow noTheme={noTheme} onClick={() => {
                        onBackClick? onBackClick() : history.goBack()
                        // history.goBack()
                    }}>{noTheme ? "Назад" : t("errorpage.back")}</ErrorButtonHollow>
                    <ErrorButtonFilled 
                        noTheme={noTheme} 
                        onClick={() => history.push(`/team-edit/${id}`)}
                    >
                        {noTheme ? "Редактировать" : t("errorpage.edit")}
                    </ErrorButtonFilled>
                </>
            )
                
            }
            {buttons === ErrorPageButtons.NOTHING && <></>}
        </ErrorButtonsWrapper>
    </ErrorPageWrapper>
  )
}
import { Colors } from '@brg/ui'
import { LayoutConst } from 'core'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { CreateLinkTo } from 'shared/constants/linkTo'
import styled, { DefaultTheme, useTheme } from 'styled-components'

const ErrorPageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`

const ErrorTitle = styled.div<{
    noTheme?: boolean
}>`
    font-family: Inter;
    font-size: 68px;
    font-weight: 600;
    line-height: 64px;
    letter-spacing: -0.02em;
    margin-top: 120px;
    text-align: center;
    color: ${p => p.noTheme ? Colors.galBlack73 : p.theme.errorPage.title};

    @media (max-width: ${LayoutConst?.screen?.mac13 || 1440}px) {
        margin-top: 176px;
        font-size: 51px;
        line-height: 60px;
    }

    @media (max-width: ${LayoutConst?.screen?.sm || 568}px) {
        margin-top: 98px;
        font-size: 28px;
        line-height: 34px;
        letter-spacing: 0px;
    }
`

const ErrorSubtitle = styled.div<{
    noTheme?: boolean
}>`
    font-family: Inter;
    font-size: 43px;
    font-weight: 600;
    line-height: 64px;
    letter-spacing: -0.02em;
    text-align: center;
    margin-top: 24px;
    color: ${p => p.noTheme ? Colors.galBlack73 : p.theme.errorPage.subtitle};

    @media (max-width: ${LayoutConst?.screen?.mac13 || 1440}px) {
        font-size: 34px;
        line-height: 41px;
        margin-top: 8px;
    }

    @media (max-width: ${LayoutConst?.screen?.sm || 568}px) {
        font-size: 17px;
        line-height: 22px;
        margin-bottom: 12px;
    }
`

const ErrorText = styled.div<{
    noTheme?: boolean
}>`
    font-family: Inter;
    font-size: 30px;
    font-weight: 400;
    line-height: 40px;
    letter-spacing: -0.02em;
    text-align: center;
    margin-top: 32px;
    color: ${p => p.noTheme ? Colors.galBlack47 : p.theme.errorPage.text};
    white-space: pre-line;

    @media (max-width: ${LayoutConst?.screen?.mac13 || 1440}px) {
        font-size: 17px;
        line-height: 22px;
        margin-top: 8px;
    }

    @media (max-width: ${LayoutConst?.screen?.sm || 568}px) {
        font-size: 13px;
        line-height: 16px;
        letter-spacing: 0px;
        margin-top: 12px;
    }
`

export const ErrorButtonsWrapper = styled.div`
    display: flex;
    gap: 24px;
    margin-top: 64px;

    @media (max-width: ${LayoutConst?.screen?.sm || 568}px) {
        gap: 10px;
        margin-top: 40px;
    }
`

const ErrorButton = styled.div<{
    noTheme?: boolean
}>`
    cursor: pointer;
    font-family: Inter;
    font-size: 17px;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: -0.02em;
    text-align: center;
    border-radius: 3px;
    padding: 12px 48px;

    @media (max-width: ${LayoutConst?.screen?.sm || 568}px) {
        padding: 12px 36.5px;
    }
`

const ErrorButtonHollow = styled(ErrorButton)`
    border: 1px solid ${p => p => p.noTheme ? Colors.galBlack31 : p.theme.errorPage.buttons.border};
    color: ${p => p.noTheme ? Colors.zel : p.theme.errorPage.buttons.hollowText};
`

const ErrorButtonFilled = styled(ErrorButton)`
    background-color: ${p => p.noTheme ? Colors.galBlack100 : p.theme.errorPage.buttons.bg};
    color: ${p => p.noTheme ? "#fff" : p.theme.errorPage.buttons.filledText};
`

export enum ErrorPageButtons {
    BACK_MAIN,
    REFRESH,
    NOTHING,
    MAIN_ONLY,
    PROFILE,
    EDIT
}

interface ErrorPageProps {
    title?: string
    subtitle?: string
    text?: string
    id?: string
    buttons?: ErrorPageButtons
    noTheme?: boolean
    onBackClick?: () => void;
}

export const ErrorPage: FC<ErrorPageProps> = ({title, subtitle, text, id, buttons = ErrorPageButtons.BACK_MAIN, noTheme, onBackClick }) => {
    const { t } = useTranslation()
    const history = useHistory()

  return (
    <ErrorPageWrapper>
        {title && <ErrorTitle noTheme={noTheme}>{title}</ErrorTitle>}
        {subtitle && <ErrorSubtitle noTheme={noTheme}>{subtitle}</ErrorSubtitle>}
        {text && <ErrorText noTheme={noTheme}>{text}</ErrorText>}
        <ErrorButtonsWrapper>
            {buttons === ErrorPageButtons.MAIN_ONLY &&
                <ErrorButtonFilled noTheme={noTheme} onClick={() => history.push("/")}>{noTheme ? "На главную" : t("errorpage.main")}</ErrorButtonFilled>
            }
            {buttons === ErrorPageButtons.BACK_MAIN &&
                <>
                    <ErrorButtonHollow noTheme={noTheme} onClick={() => {
                        onBackClick? onBackClick() : history.goBack()
                        // history.goBack()
                    }}>{noTheme ? "Назад" : t("errorpage.back")}</ErrorButtonHollow>
                    <ErrorButtonFilled noTheme={noTheme} onClick={() =>{
                        onBackClick && onBackClick()
                        history.push("/")
                    }}>{noTheme ? "На главную" : t("errorpage.main")}</ErrorButtonFilled>
                </>
            }
            {buttons === ErrorPageButtons.REFRESH &&
                <ErrorButtonFilled noTheme={noTheme} onClick={() => window.location.reload()}>{noTheme ? "Обновить" : t("errorpage.refresh")}</ErrorButtonFilled>
            }
            {buttons === ErrorPageButtons.PROFILE && (
                <>
                     <ErrorButtonHollow noTheme={noTheme} onClick={() => {
                        onBackClick? onBackClick() : history.goBack()
                        // history.goBack()
                    }}>{noTheme ? "Назад" : t("errorpage.back")}</ErrorButtonHollow>
                    <ErrorButtonFilled 
                        noTheme={noTheme} 
                        onClick={() => history.push(CreateLinkTo.designer(id ?? ''))}
                    >
                        {noTheme ? "Профиль" : t("errorpage.profile")}
                    </ErrorButtonFilled>
                </>
            )
                
            }
            {buttons === ErrorPageButtons.EDIT && (
                <>
                     <ErrorButtonHollow noTheme={noTheme} onClick={() => {
                        onBackClick? onBackClick() : history.goBack()
                        // history.goBack()
                    }}>{noTheme ? "Назад" : t("errorpage.back")}</ErrorButtonHollow>
                    <ErrorButtonFilled 
                        noTheme={noTheme} 
                        onClick={() => history.push(`/team-edit/${id}`)}
                    >
                        {noTheme ? "Редактировать" : t("errorpage.edit")}
                    </ErrorButtonFilled>
                </>
            )
                
            }
            {buttons === ErrorPageButtons.NOTHING && <></>}
        </ErrorButtonsWrapper>
    </ErrorPageWrapper>
  )
}
